import { useState, useEffect } from 'react'
import { Card, Button, Table, Badge, Modal, Form, Row, Col, Spinner, Alert, ProgressBar } from 'react-bootstrap'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import api from '../../services/api'
import { formatDateOnlyEsMX, formatDateTimeEsMX } from '../../utils/apiDateFormat'
import { formatCurrency } from '../../utils/formatters'
import DescargaSATPanel from './DescargaSATPanel'

const XMLProcessing = () => {
  const { user: _user } = useAuth()
  const queryClient = useQueryClient()
  const [showUpload, setShowUpload] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [, setSelectedXml] = useState(null)
  const [showDetail, setShowDetail] = useState(false)
  const [xmlDetail, setXmlDetail] = useState(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [xmlToDelete, setXmlToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [uploadResults, setUploadResults] = useState(null)
  const [showResults, setShowResults] = useState(false)

  // Lista del último batch cargado (se reinicia en cada subida exitosa)
  const [lastUploadedItems, setLastUploadedItems] = useState([])
  // Datos enriquecidos para la tabla (RFC, nombres, fecha)
  const [enrichedItems, setEnrichedItems] = useState([])
  const [loadingEnriched, setLoadingEnriched] = useState(false)

  // Obtener detalles de cada ítem para mostrar en la tabla
  useEffect(() => {
    if (lastUploadedItems.length === 0) {
      setEnrichedItems([])
      return
    }
    setLoadingEnriched(true)
    const fetchAll = async () => {
      const results = await Promise.all(
        lastUploadedItems.map(async (item) => {
          try {
            const endpoint = item.tipo === 'bill'
              ? `/financial/bills/${item.id}/`
              : `/financial/invoices/${item.id}/`
            const { data } = await api.get(endpoint)
            const isBill = item.tipo === 'bill'
            return {
              ...item,
              fecha: isBill ? data.emission_date : data.issue_date,
              emisor_rfc: isBill ? data.provider_rfc : data.client_rfc,
              emisor_nombre: isBill ? data.provider_razon_social : (data.client_name || ''),
              receptor_rfc: isBill ? (data.client_rfc || data.client?.rfc) : (data.receptor_rfc || data.receptor_rfc_display || ''),
              receptor_nombre: isBill ? data.client_name : (data.receptor_name || data.receptor_razon_social || '')
            }
          } catch {
            return { ...item, fecha: null, emisor_rfc: '', emisor_nombre: '', receptor_rfc: '', receptor_nombre: '' }
          }
        })
      )
      setEnrichedItems(results)
      setLoadingEnriched(false)
    }
    fetchAll()
  }, [lastUploadedItems])

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files)
    const xmlFiles = files.filter(file => file.name.endsWith('.xml'))
    setSelectedFiles(prev => [...prev, ...xmlFiles])
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files)
      const xmlFiles = files.filter(file => file.name.endsWith('.xml'))
      setSelectedFiles(prev => [...prev, ...xmlFiles])
    }
  }

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Debes seleccionar al menos un archivo XML')
      return
    }

    setUploading(true)
    setUploadProgress(0)
    const results = {
      total: selectedFiles.length,
      exitosos: 0,
      errores: 0,
      duplicados: 0,
      ppdPendientes: 0,
      resultados: []
    }

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i]
        const formData = new FormData()
        formData.append('archivo', file)

        try {
          const response = await api.post('/accounting/xml-cfdis/upload/', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                ((i + progressEvent.loaded / progressEvent.total) / selectedFiles.length) * 100
              )
              setUploadProgress(percentCompleted)
            }
          })

          results.exitosos++
          const tipo = response.data.tipo || 'invoice'
          const tipoLabel =
            tipo === 'ambos'
              ? 'Recibida y emitida'
              : tipo === 'bill'
                ? 'Factura recibida'
                : 'Factura emitida'
          if (response.data.ppd_requiere_seguimiento) {
            results.ppdPendientes = (results.ppdPendientes || 0) + 1
          }
          results.resultados.push({
            id: response.data.id,
            billId: response.data.bill_id,
            invoiceId: response.data.invoice_id,
            tipo: tipo === 'ambos' ? 'ambos' : (tipo === 'bill' ? 'bill' : 'invoice'),
            tipoLabel,
            nombre: file.name,
            estado: 'correcto',
            uuid: response.data.uuid || 'N/A',
            message: response.data.message,
            ppd: !!response.data.ppd_requiere_seguimiento,
            tipoFactura: response.data.tipo_factura || '',
          })
        } catch (error) {
          // Detectar si es un XML duplicado (409 Conflict)
          if (error.response?.status === 409 || error.response?.data?.duplicate) {
            results.duplicados++
            results.resultados.push({
              nombre: file.name,
              estado: 'duplicado',
              motivo: error.response?.data?.error || 'Este XML ya fue cargado previamente'
            })
          } else {
            results.errores++
            const errorMessage = error.response?.data?.error || 'Error desconocido al procesar el archivo'
            results.resultados.push({
              nombre: file.name,
              estado: 'error',
              motivo: errorMessage
            })
          }
        }
      }

      setUploadResults(results)
      setShowResults(true)
      // Actualizar lista con solo el último batch cargado (solo los exitosos)
      const exitosos = results.resultados
        .filter(r => r.estado === 'correcto')
        .flatMap((r) => {
          if (r.tipo === 'ambos') {
            return [
              { id: r.billId, tipo: 'bill', uuid: r.uuid, nombre: r.nombre },
              { id: r.invoiceId, tipo: 'invoice', uuid: r.uuid, nombre: r.nombre },
            ]
          }
          return [{ id: r.id, tipo: r.tipo, uuid: r.uuid, nombre: r.nombre }]
        })
      setLastUploadedItems(exitosos)

      queryClient.invalidateQueries('invoices')
      queryClient.invalidateQueries('bills')
      
      if (results.errores === 0 && results.duplicados === 0) {
        toast.success(`${results.exitosos} archivo(s) subido(s) exitosamente`)
      } else {
        const parts = []
        if (results.exitosos > 0) parts.push(`${results.exitosos} exitoso(s)`)
        if (results.duplicados > 0) parts.push(`${results.duplicados} duplicado(s)`)
        if (results.errores > 0) parts.push(`${results.errores} error(es)`)
        toast.warning(parts.join(', '))
      }
    } catch (error) {
      toast.error('Error inesperado durante la carga')
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const getEstadoBadge = (estado) => {
    const badges = {
      'pendiente': { bg: 'warning', text: 'Pendiente' },
      'procesado': { bg: 'success', text: 'Procesado' },
      'error': { bg: 'danger', text: 'Error' }
    }
    // Normalizar a minúsculas para comparación
    const estadoLower = (estado || '').toLowerCase()
    const badge = badges[estadoLower] || { bg: 'secondary', text: estado || 'N/A' }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const handleViewDetail = async (xml) => {
    setSelectedXml(xml.id)
    setShowDetail(true)
    setLoadingDetail(true)
    setXmlDetail(null)

    try {
      const endpoint = xml.tipo === 'bill'
        ? `/financial/bills/${xml.id}/`
        : `/financial/invoices/${xml.id}/`
      const response = await api.get(endpoint)
      const data = response.data
      const conceptos = data.conceptos || []
      const isBill = xml.tipo === 'bill'
      setXmlDetail({
        uuid: data.uuid || data.cfdi_uuid,
        fecha: data.emission_date || data.issue_date,
        emisor_rfc: isBill ? data.provider_rfc : data.client_rfc,
        emisor_nombre: isBill ? data.provider_razon_social : (data.client_name || ''),
        receptor_rfc: isBill ? (data.client_rfc || data.client?.rfc) : (data.receptor_rfc || data.receptor_rfc_display || ''),
        receptor_nombre: isBill ? data.client_name : (data.receptor_name || data.receptor_razon_social || ''),
        cfdi_type: isBill ? 'RECIBIDO' : 'EMITIDO',
        total: data.total,
        subtotal: data.subtotal,
        tax_amount: data.tax_amount,
        estado: data.is_processed || data.is_processed_contablemente ? 'procesado' : 'pendiente',
        is_deductible: true,
        conceptos: conceptos.map(c => ({
          descripcion: c.descripcion,
          cantidad: c.cantidad,
          valor_unitario: c.precio_unitario,
          importe: c.importe,
          importe_traslado: c.iva_trasladado
        }))
      })
    } catch (error) {
      toast.error('Error al cargar los detalles')
      console.error(error)
    } finally {
      setLoadingDetail(false)
    }
  }

  const handleDeleteClick = (xml) => {
    setXmlToDelete(xml)
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (!xmlToDelete) return

    setDeleting(true)
    try {
      const endpoint = xmlToDelete.tipo === 'bill'
        ? `/financial/bills/${xmlToDelete.id}/`
        : `/financial/invoices/${xmlToDelete.id}/`
      await api.delete(endpoint)
      toast.success('Eliminado correctamente')
      setLastUploadedItems(prev => prev.filter(item => !(item.tipo === xmlToDelete.tipo && item.id === xmlToDelete.id)))
      queryClient.invalidateQueries('invoices')
      queryClient.invalidateQueries('bills')
      setShowDeleteConfirm(false)
      setXmlToDelete(null)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al eliminar')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Procesamiento de XML CFDI</h3>
        <Button variant="primary" onClick={() => setShowUpload(true)}>
          + Subir XMLs
        </Button>
      </div>

      {lastUploadedItems.length > 0 ? (
        <Card>
          <Card.Body>
            <p className="text-muted small mb-3">
              Último batch cargado. Gestiona los comprobantes en <strong>Finanzas → Facturas</strong> o <strong>Facturas Recibidas</strong>.
            </p>
            <Table responsive>
              <thead>
                <tr>
                  <th>Archivo</th>
                  <th>UUID</th>
                  <th>Fecha</th>
                  <th>RFC Emisor</th>
                  <th>Nombre Emisor</th>
                  <th>RFC Receptor</th>
                  <th>Nombre Receptor</th>
                  <th>Tipo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {(loadingEnriched ? lastUploadedItems : enrichedItems).map((item) => (
                  <tr key={`${item.tipo}-${item.id}`}>
                    <td>{item.nombre}</td>
                    <td><code>{item.uuid || 'N/A'}</code></td>
                    <td>
                      {loadingEnriched ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        item.fecha ? formatDateOnlyEsMX(item.fecha, {}, '—') : '—'
                      )}
                    </td>
                    <td>{loadingEnriched ? '...' : (item.emisor_rfc || '—')}</td>
                    <td>{loadingEnriched ? '...' : (item.emisor_nombre || '—')}</td>
                    <td>{loadingEnriched ? '...' : (item.receptor_rfc || '—')}</td>
                    <td>{loadingEnriched ? '...' : (item.receptor_nombre || '—')}</td>
                    <td>
                      {item.tipo === 'bill' ? (
                        <Badge bg="info">Factura recibida</Badge>
                      ) : (
                        <Badge bg="primary">Factura emitida</Badge>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewDetail(item)}
                        >
                          Ver Detalle
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteClick(item)}
                          title="Eliminar (no se puede si tiene pagos aplicados)"
                        >
                          Eliminar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      ) : (
        <Alert variant="info">
          No hay comprobantes en el último batch. Sube XMLs para ver aquí los que se procesen correctamente.
        </Alert>
      )}

      {/* Modal de detalle */}
      <Modal show={showDetail} onHide={() => { setShowDetail(false); setSelectedXml(null); setXmlDetail(null) }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detalle del CFDI</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetail ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Cargando...</span>
              </Spinner>
            </div>
          ) : xmlDetail ? (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>UUID:</strong> {xmlDetail.uuid}
                </Col>
                <Col md={6}>
                  <strong>Fecha de Emisión:</strong> {xmlDetail.fecha ? formatDateTimeEsMX(xmlDetail.fecha, undefined, 'N/A') : 'N/A'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>RFC Emisor:</strong> {xmlDetail.emisor_rfc || 'N/A'}
                </Col>
                <Col md={6}>
                  <strong>Nombre Emisor:</strong> {xmlDetail.emisor_nombre || 'N/A'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>RFC Receptor:</strong> {xmlDetail.receptor_rfc || 'N/A'}
                </Col>
                <Col md={6}>
                  <strong>Nombre Receptor:</strong> {xmlDetail.receptor_nombre || 'N/A'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Tipo:</strong> {xmlDetail.cfdi_type || 'N/A'}
                </Col>
                <Col md={6}>
                  <strong>Total:</strong> {formatCurrency(xmlDetail.total || 0)}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Subtotal:</strong> {formatCurrency(xmlDetail.subtotal || 0)}
                </Col>
                <Col md={6}>
                  <strong>Impuesto:</strong> {formatCurrency(xmlDetail.tax_amount || 0)}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Estado:</strong> {getEstadoBadge(xmlDetail.estado)}
                </Col>
                <Col md={6}>
                  <strong>Deducible:</strong> {xmlDetail.is_deductible ? 'Sí' : 'No'}
                </Col>
              </Row>

              {xmlDetail.conceptos && xmlDetail.conceptos.length > 0 && (
                <div className="mt-4">
                  <h5>Conceptos</h5>
                  <Table responsive size="sm" className="mt-2">
                    <thead>
                      <tr>
                        <th>Descripción</th>
                        <th>Cantidad</th>
                        <th>Valor Unitario</th>
                        <th>Importe</th>
                        <th>IVA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {xmlDetail.conceptos.map((concepto, idx) => (
                        <tr key={idx}>
                          <td>{concepto.descripcion || 'N/A'}</td>
                          <td>{parseFloat(concepto.cantidad || 0).toFixed(2)}</td>
                          <td>{formatCurrency(concepto.valor_unitario || 0)}</td>
                          <td>{formatCurrency(concepto.importe || 0)}</td>
                          <td>{formatCurrency(concepto.importe_traslado || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          ) : (
            <Alert variant="warning">No se pudieron cargar los detalles</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowDetail(false); setSelectedXml(null); setXmlDetail(null) }}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de confirmación de eliminación */}
      <Modal show={showDeleteConfirm} onHide={() => { setShowDeleteConfirm(false); setXmlToDelete(null) }}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>¿Está seguro de eliminar este comprobante?</p>
          {xmlToDelete && (
            <div className="mb-3">
              <strong>Archivo:</strong> {xmlToDelete.nombre}
              <br />
              <strong>UUID:</strong> {xmlToDelete.uuid}
            </div>
          )}
          <Alert variant="warning" className="mb-0">
            No se puede eliminar si tiene pagos aplicados. Se eliminarán transacciones contables relacionadas si las hubiera.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowDeleteConfirm(false); setXmlToDelete(null) }} disabled={deleting}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de resultados de carga */}
      <Modal show={showResults} onHide={() => { setShowResults(false); setShowUpload(false); setSelectedFiles([]); setUploadResults(null) }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Resultados de Carga</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {uploadResults && (
            <div>
              <Alert variant={uploadResults.errores === 0 && uploadResults.duplicados === 0 ? 'success' : 'warning'} className="mb-3">
                <strong>Resumen:</strong> {
                  [
                    uploadResults.exitosos > 0 && `${uploadResults.exitosos} exitoso(s)`,
                    uploadResults.duplicados > 0 && `${uploadResults.duplicados} duplicado(s)`,
                    uploadResults.errores > 0 && `${uploadResults.errores} error(es)`
                  ].filter(Boolean).join(', ')
                } de {uploadResults.total} archivo(s)
                {uploadResults.ppdPendientes > 0 && (
                  <div className="mt-2 mb-0">
                    <Badge bg="info">{uploadResults.ppdPendientes} PPD</Badge>
                    {' '}requieren seguimiento de complementos (filtro «PPD pendientes» en Facturas).
                  </div>
                )}
              </Alert>
              <Table responsive size="sm">
                <thead>
                  <tr>
                    <th>Archivo</th>
                    <th>Estado</th>
                    <th>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadResults.resultados.map((resultado, idx) => (
                    <tr key={idx}>
                      <td>{resultado.nombre}</td>
                      <td>
                        {resultado.estado === 'correcto' ? (
                          <Badge bg="success">✓ Correcto</Badge>
                        ) : resultado.estado === 'duplicado' ? (
                          <Badge bg="warning">⚠ Duplicado</Badge>
                        ) : (
                          <Badge bg="danger">✗ Error</Badge>
                        )}
                      </td>
                      <td>
                        {resultado.estado === 'correcto' ? (
                          <small className="text-muted">{resultado.tipoLabel || ''} — UUID: {resultado.uuid}</small>
                        ) : (
                          <small className={resultado.estado === 'duplicado' ? 'text-warning' : 'text-danger'}>
                            {resultado.motivo}
                          </small>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => { setShowResults(false); setShowUpload(false); setSelectedFiles([]); setUploadResults(null) }}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de upload */}
      <Modal show={showUpload} onHide={() => { setShowUpload(false); setSelectedFiles([]) }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Subir Archivos XML</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Zona de drag & drop */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${dragActive ? '#0d6efd' : '#dee2e6'}`,
              borderRadius: '8px',
              padding: '40px',
              textAlign: 'center',
              backgroundColor: dragActive ? '#f0f7ff' : '#f8f9fa',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
          >
            <div className="mb-3">
              <i className="bi bi-cloud-upload" style={{ fontSize: '3rem', color: dragActive ? '#0d6efd' : '#6c757d' }}></i>
            </div>
            <p className="mb-2">
              {dragActive ? 'Suelta los archivos aquí' : 'Arrastra y suelta archivos XML aquí'}
            </p>
            <p className="text-muted small mb-3">o</p>
            <Form.Group>
              <Form.Control
                type="file"
                accept=".xml"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id="file-upload-input"
              />
              <Button
                variant="outline-primary"
                onClick={() => document.getElementById('file-upload-input').click()}
              >
                Seleccionar Archivos XML
              </Button>
            </Form.Group>
            <Form.Text className="text-muted d-block mt-2">
              Puedes seleccionar múltiples archivos XML
            </Form.Text>
          </div>

          {/* Lista de archivos seleccionados */}
          {selectedFiles.length > 0 && (
            <div className="mt-4">
              <h6>Archivos Seleccionados ({selectedFiles.length}):</h6>
              <div className="list-group" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {selectedFiles.map((file, idx) => (
                  <div key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                    <span>{file.name}</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0"
                      onClick={() => {
                        const newFiles = [...selectedFiles]
                        newFiles.splice(idx, 1)
                        setSelectedFiles(newFiles)
                      }}
                      disabled={uploading}
                    >
                      ✕
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {uploading && (
            <div className="mt-3">
              <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowUpload(false); setSelectedFiles([]) }} disabled={uploading}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleUpload} disabled={uploading || selectedFiles.length === 0}>
            {uploading ? 'Subiendo...' : `Subir ${selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}`}
          </Button>
        </Modal.Footer>
      </Modal>

      <DescargaSATPanel />
    </div>
  )
}

export default XMLProcessing

